import Head from 'next/head';
import { useState, useEffect, useContext } from 'react';

import { useRouter } from 'next/router';
import ReCAPTCHA from 'react-google-recaptcha';
import { UserContext } from '../components/Navigation';
import { ClipLoader } from 'react-spinners';
import { useTheme } from 'next-themes';
import Icon from '../components/Icon';
import { Notice } from '../components/auth/AuthLayout';
import { Field, Input, Select } from '../components/Form';
import { display } from '../lib/fonts';
import { page as pageBg, card, btn, muted, body } from '../lib/ui';

export default function InterviewPrep() {
    const router = useRouter();
    const { resolvedTheme } = useTheme();
    const { user, loading: authLoading, isLoggingOut } = useContext(UserContext);
    const [isLoading, setIsLoading] = useState(true);

    // Form state
    const [name, setName] = useState('');
    const [experienceLevel, setExperienceLevel] = useState(0);
    const [industry, setIndustry] = useState('Software Development');
    const [generatedQuestions, setGeneratedQuestions] = useState('');
    const [loading, setLoading] = useState(false);
    const [customIndustry, setCustomIndustry] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [captchaToken, setCaptchaToken] = useState(null);
    const [hasClickedGenerate, setHasClickedGenerate] = useState(false);

    useEffect(() => {
        // If not loading and user is not logged in, redirect to login
        if (!authLoading && !user && !isLoggingOut) {
            // Redirect to appropriate login based on the user type trying to access
            const loginPath = router.query.role === 'referrer' ? '/login?role=referrer' : '/login?role=job-seeker';
            router.push(loginPath);
        } else if (!authLoading && user) {
            // Pre-fill name if user is logged in
            if (user.name) {
                setName(user.name);
            }
            setIsLoading(false);
        }
    }, [authLoading, user, router, isLoggingOut]);

    // Load initial state from sessionStorage
    useEffect(() => {
        if (typeof window !== 'undefined') {
            const savedExp = sessionStorage.getItem('ai_interview_experience');
            const savedIndustry = sessionStorage.getItem('ai_interview_industry');
            const savedCustom = sessionStorage.getItem('ai_interview_custom_industry');
            const savedQuestions = sessionStorage.getItem('ai_interview_generated_questions');

            if (savedExp) setExperienceLevel(Number(savedExp));
            if (savedIndustry) setIndustry(savedIndustry);
            if (savedCustom) setCustomIndustry(savedCustom);
            if (savedQuestions) setGeneratedQuestions(savedQuestions);
        }
    }, []);

    // Save state to sessionStorage when it changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('ai_interview_experience', experienceLevel.toString());
            sessionStorage.setItem('ai_interview_industry', industry);
            sessionStorage.setItem('ai_interview_custom_industry', customIndustry);
            sessionStorage.setItem('ai_interview_generated_questions', generatedQuestions);
        }
    }, [experienceLevel, industry, customIndustry, generatedQuestions]);

    // Disable the generate button after first click
    const handleGenerateClick = async () => {
        setHasClickedGenerate(true);
        await handleGenerateQuestions();
    };

    if (authLoading || isLoading || isLoggingOut) {
        return (
            <div className={`${pageBg} flex items-center justify-center`}>
                <ClipLoader size={40} color="#00b4c6" />
            </div>
        );
    }

    if (!user) {
        return null; // Will redirect from useEffect
    }

    const handleIndustryChange = (e) => {
        const value = e.target.value;
        setIndustry(value);

        if (value !== 'Custom') {
            setCustomIndustry('');
        }
    };

    const handleGenerateQuestions = async () => {
        // Check if any required fields are missing and CAPTCHA is completed
        if (!name || (industry === 'Custom' && !customIndustry) || !captchaToken) {
            setErrorMessage('Please fill out all fields and complete the CAPTCHA before generating questions.');
            return;
        }


        setErrorMessage(''); // Clear error message
        setLoading(true); // Start spinner when API call begins

        const selectedIndustry = industry === 'Custom' ? customIndustry : industry;

        const prompt = `Hi I am ${name}, and I am from ${selectedIndustry}. Please tell me interview questions for level of experience - ${experienceLevel} for me.`;

        const response = await fetch('/api/ai_interview', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ question: prompt, captchaToken }), // Add captchaToken to the request body
        });

        const data = await response.json();

        if (response.ok) {
            const parsedContent = formatGeminiResponse(data.generatedContent.candidates[0].content.parts[0].text);
            setGeneratedQuestions(parsedContent);
        } else {
            console.error('Error generating questions:', data.error);
        }

        setLoading(false);
    };

    // Check if the form is valid (all fields filled)
    const isFormValid = name && (industry !== 'Custom' || customIndustry);

    return (
        <>
            <Head>
                <title>AI Interview Prep | Mock Interview Questions | Doogler</title>
                <meta name="description" content="Prepare for your next job interview with our AI-powered mock interview tool. Get personalized questions based on your experience level and industry stack." />
                <meta name="keywords" content="AI interview, mock interview, interview preparation, interview questions, career coaching, job interview tips" />
                <meta name="robots" content="index, follow" />
                <meta property="og:title" content="AI Interview Preparation | Personalized Mock Interviews | Doogler" />
                <meta property="og:description" content="Master your interview skills with AI-generated questions tailored specifically to your career profile." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://doogler.in/AI_mock_interview" />
                <meta property="og:image" content="https://doogler.in/employee.svg" />
                <link rel="canonical" href="https://doogler.in/AI_mock_interview" />
            </Head>
            <main className={`${pageBg} px-4 py-8 sm:px-6 md:py-12 lg:px-8`}>
                <div className="mx-auto max-w-3xl">
                    {/* Header Section */}
                    <header className="mb-8 text-center md:mb-10">
                        <h1 className={`${display.className} text-3xl font-bold tracking-tight text-slate-900 dark:text-white md:text-5xl text-balance`}>
                            AI Interview Preparation
                        </h1>
                        <p className={`mx-auto mt-3 max-w-2xl ${body}`}>
                            Get personalized interview questions powered by AI to ace your next interview
                        </p>
                    </header>

                    {/* Main Form Card */}
                    <div className={`${card} p-5 sm:p-8`}>
                        <div className="space-y-5">
                            <Field label="Your Name" htmlFor="ai-name">
                                <Input id="ai-name" type="text" value={name} disabled className="cursor-not-allowed" placeholder="Your name" />
                            </Field>

                            <Field label="Years of Experience" htmlFor="ai-exp">
                                <Input
                                    id="ai-exp"
                                    type="number"
                                    inputMode="numeric"
                                    value={experienceLevel}
                                    onChange={(e) => setExperienceLevel(Number(e.target.value))}
                                    min="0"
                                    placeholder="0"
                                />
                            </Field>

                            <Field label="Industry / Tech Stack" htmlFor="ai-industry">
                                <Select id="ai-industry" value={industry} onChange={handleIndustryChange}>
                                    <option value="Software Development">Software Development</option>
                                    <option value="QA">QA / Testing</option>
                                    <option value="Business Analyst">Business Analyst</option>
                                    <option value="Finance">Finance</option>
                                    <option value="Data Science">Data Science</option>
                                    <option value="DevOps">DevOps</option>
                                    <option value="Product Management">Product Management</option>
                                    <option value="Custom">Custom (Specify below)</option>
                                </Select>

                                {industry === 'Custom' && (
                                    <div className="mt-3">
                                        <label htmlFor="ai-custom" className="sr-only">Custom industry or tech stack</label>
                                        <Input
                                            id="ai-custom"
                                            type="text"
                                            value={customIndustry}
                                            onChange={(e) => setCustomIndustry(e.target.value)}
                                            placeholder="Enter your custom industry or tech stack"
                                        />
                                    </div>
                                )}
                            </Field>

                            {errorMessage && <Notice type="error">{errorMessage}</Notice>}

                            {/* CAPTCHA */}
                            <div className="flex justify-center overflow-hidden py-2">
                                <div className="origin-center scale-[0.85] md:scale-100">
                                    <ReCAPTCHA
                                        sitekey={
                                            process.env.NODE_ENV === 'development'
                                                ? '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'
                                                : process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
                                        }
                                        onChange={(token) => setCaptchaToken(token)}
                                        theme={resolvedTheme === 'dark' ? 'dark' : 'light'}
                                    />
                                </div>
                            </div>

                            {/* Generate Button */}
                            <button
                                onClick={handleGenerateClick}
                                disabled={!isFormValid || !captchaToken || hasClickedGenerate}
                                className={`${btn.primary} w-full min-h-[52px] text-base`}
                            >
                                {hasClickedGenerate ? (
                                    <>
                                        <Icon name="check" className="w-5 h-5" strokeWidth={2.2} />
                                        Questions Generated
                                    </>
                                ) : (
                                    'Generate Interview Questions'
                                )}
                            </button>
                        </div>

                        {/* Loading Spinner */}
                        {loading && (
                            <div className="flex flex-col items-center justify-center py-10" role="status">
                                <div className="h-10 w-10 animate-spin rounded-full border-4 border-customBlue/25 border-t-customBlue" />
                                <p className={`mt-4 ${body}`}>Generating your personalized questions...</p>
                            </div>
                        )}
                    </div>

                    {/* Generated Questions Display */}
                    {!loading && generatedQuestions && (
                        <section className={`${card} mt-6 overflow-hidden`}>
                            <div className="border-b border-slate-200 dark:border-gray-800 px-5 py-4 sm:px-8 sm:py-5">
                                <h2 className={`${display.className} text-xl font-bold text-slate-900 dark:text-white sm:text-2xl`}>
                                    Your Interview Questions
                                </h2>
                                <p className={`mt-1 ${muted}`}>
                                    Tailored for {industry === 'Custom' ? customIndustry : industry} &middot; {experienceLevel} years experience
                                </p>
                            </div>

                            <div className="p-5 sm:p-8">
                                <div
                                    className="max-w-none text-base leading-relaxed text-slate-700 dark:text-slate-300 [&_h3]:mb-2 [&_h3]:border-slate-200 [&_h3]:text-slate-900 dark:[&_h3]:border-gray-800 dark:[&_h3]:text-white [&_strong]:font-semibold [&_strong]:text-slate-900 dark:[&_strong]:text-white [&_ul]:my-2"
                                    dangerouslySetInnerHTML={{
                                        __html: formatGeminiResponse(generatedQuestions),
                                    }}
                                />
                            </div>

                            <div className="flex flex-col gap-3 border-t border-slate-200 dark:border-gray-800 bg-slate-50 dark:bg-gray-950/40 px-5 py-4 sm:flex-row sm:px-8">
                                <button
                                    onClick={() => {
                                        const element = document.createElement('a');
                                        const file = new Blob([generatedQuestions.replace(/<[^>]*>/g, '')], { type: 'text/plain' });
                                        element.href = URL.createObjectURL(file);
                                        element.download = `interview-questions-${Date.now()}.txt`;
                                        element.click();
                                    }}
                                    className={`${btn.primary} flex-1`}
                                >
                                    Download Questions
                                </button>
                                <button onClick={() => window.print()} className={`${btn.secondary} flex-1`}>
                                    Print
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            </main>
        </>
    );
}


// Utility function to parse and beautify the Gemini response
function formatGeminiResponse(text) {
    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');

    formattedText = formattedText
        .replace(/## (.*?)(?=\n)/g, '<h3 class="text-xl font-semibold border-b pb-1 mt-4">$1</h3>')
        .replace(/\n\n\*\ (.*?)\n/g, '<ul class="list-disc ml-6"><li>$1</li></ul>')
        .replace(/\n\*\ (.*?)\n/g, '<ul class="list-disc ml-6"><li>$1</li></ul>')
        .replace(/(\*.*?:\*)/g, '<span class="block mt-2 text-lg font-medium">$1</span>')
        .replace(/\n/g, '<br />'); // Replace newlines with HTML breaks for proper spacing

    return formattedText;
}
